import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import useSWRMutation from 'swr/mutation'

import { apiPostFetcher } from '@/common/api/fetcher'
import { LoginResponse, LoginVars } from '@/common/api/params/auth'
import { ApiPath } from '@/common/api/path'
import { statusCodeErrorMessage } from '@/common/app/error'
import { PASSWORD_CAPTION } from '@/common/form/schema'
import { HttpStatusCode } from '@/common/util/status'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import FormRow from '@/components/view/layouts/form/FormRow'

import FormCheckBox from '@/components/container/form/widgets/FormCheckBox'
import FormError from '@/components/container/form/widgets/FormError'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'

import {
  LoginFormSchemaType,
  loadRememberedLogin,
  loginSchema,
  saveRememberedLogin
} from './loginSchema'

interface Props {
  onRequested: (loginResponse: LoginResponse) => void
}

const LoginForm = ({ onRequested }: Props) => {
  const [status, setStatus] = useState<HttpStatusCode | null>(null)

  const remembered = loadRememberedLogin()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormSchemaType>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      projectNumber: remembered?.projectNumber ?? '',
      email: remembered?.email ?? '',
      password: '',
      remember: !!remembered
    }
  })

  const { trigger } = useSWRMutation<LoginResponse, Response, string, LoginVars>(
    ApiPath.LOGIN,
    apiPostFetcher,
    {
      onSuccess: (data) => {
        onRequested(data)
      },
      onError: (error) => {
        setStatus(error.status as HttpStatusCode)
      }
    }
  )

  const onSubmit: SubmitHandler<LoginFormSchemaType> = (data) => {
    saveRememberedLogin(data.remember, {
      projectNumber: data.projectNumber,
      email: data.email
    })
    return trigger({
      project_number: data.projectNumber,
      email: data.email,
      password: data.password
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='flex w-full flex-col gap-1.5'>
        {status && (
          <FormError
            message={
              status == HttpStatusCode.UNAUTHORIZED
                ? 'プロジェクト番号・メールアドレス・パスワードのいずれかが正しくありません。'
                : statusCodeErrorMessage(status)
            }
          />
        )}
        <FormRow direction='vertical' gap={2}>
          <FormLabel>プロジェクト番号</FormLabel>
          <FormTextField
            register={register('projectNumber')}
            error={!!errors.projectNumber?.message}
            size={TextBoxSize.FULL}
          />
          <FormError message={errors.projectNumber?.message} />
        </FormRow>
        <FormRow direction='vertical' gap={2}>
          <FormLabel>メールアドレス</FormLabel>
          <FormTextField
            register={register('email')}
            error={!!errors.email?.message}
            size={TextBoxSize.FULL}
          />
          <FormError message={errors.email?.message} />
        </FormRow>
        <FormRow direction='vertical' gap={2}>
          <FormLabel>パスワード</FormLabel>
          <FormTextField
            type={FormTextFieldType.PASSWORD}
            register={register('password')}
            error={!!errors.password?.message}
            size={TextBoxSize.FULL}
          />
          <FormError message={errors.password?.message} />
          <FormCaption>{PASSWORD_CAPTION}</FormCaption>
        </FormRow>
        <FormRow direction='vertical' gap={2}>
          <FormCheckBox
            control={control}
            name='remember'
            label='プロジェクト番号とメールアドレスを記憶する'
          />
        </FormRow>
        <div className={'mt-8'}>
          <Button
            type={'submit'}
            size={'full'}
            loading={isSubmitting}
            color={ButtonColor.PRIMARY_BRAND}
          >
            <Text size={16} color={TextColor.ON_BG_FILL}>
              ログイン
            </Text>
          </Button>
        </div>
      </div>
    </form>
  )
}

export default LoginForm
